/*
 * =========================================================
 * CHEMARRY CAT FOOD v1.0
 * Alimentación para gatos: Farmina N&D + Vet Life
 * Compatible con Shopify Dawn 15.4.1
 * =========================================================
 */

(() => {
  'use strict';

  if (customElements.get('chemarry-cat-food')) return;

  class ChemarryCatFood extends HTMLElement {
    constructor() {
      super();

      this.activeLine = '';
      this.activeFilter = 'all';
      this.searchTerm = '';
      this.toastTimer = null;

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleFilterClick = this.handleFilterClick.bind(this);
      this.handleSearch = this.handleSearch.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    /* =====================================================
       1. INICIALIZACIÓN
       ===================================================== */

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.cacheElements();
      this.setInitialState();
      this.bindEvents();
      this.updateInterface();
    }

    disconnectedCallback() {
      this.unbindEvents();

      if (this.toastTimer) {
        window.clearTimeout(this.toastTimer);
      }

      delete this.dataset.initialized;
    }

    cacheElements() {
      this.tabs = Array.from(
        this.querySelectorAll('[data-food-tab]')
      );

      this.filterGroups = Array.from(
        this.querySelectorAll('[data-food-filter-group]')
      );

      this.filters = Array.from(
        this.querySelectorAll('[data-food-filter]')
      );

      this.cards = Array.from(
        this.querySelectorAll('[data-food-card]')
      );

      this.searchInput = this.querySelector(
        '[data-food-search]'
      );

      this.countElement = this.querySelector(
        '[data-food-count]'
      );

      this.grid = this.querySelector(
        '[data-food-grid]'
      );

      this.previousButton = this.querySelector(
        '[data-food-previous]'
      );

      this.nextButton = this.querySelector(
        '[data-food-next]'
      );

      this.dietaryNotice = this.querySelector(
        '[data-food-dietary-notice]'
      );

      this.emptyState = this.querySelector(
        '[data-food-empty]'
      );

      this.toast = this.querySelector(
        '[data-food-toast]'
      );

      this.forms = Array.from(
        this.querySelectorAll('[data-food-form]')
      );

      this.variantSelectors = Array.from(
        this.querySelectorAll('[data-food-variant-select]')
      );
    }

    setInitialState() {
      const selectedTab =
        this.tabs.find(
          (tab) => tab.getAttribute('aria-selected') === 'true'
        ) || this.tabs[0];

      if (selectedTab) {
        this.activeLine =
          selectedTab.dataset.foodTab || '';
      } else if (this.cards[0]) {
        this.activeLine =
          this.cards[0].dataset.foodLine || '';
      }

      this.activeFilter = 'all';
      this.searchTerm = '';

      if (this.searchInput) {
        this.searchInput.value = '';
      }
    }

    /* =====================================================
       2. EVENTOS
       ===================================================== */

    bindEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
      });

      this.filters.forEach((filter) => {
        filter.addEventListener(
          'click',
          this.handleFilterClick
        );
      });

      if (this.searchInput) {
        this.searchInput.addEventListener(
          'input',
          this.handleSearch
        );
      }

      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          this.handlePrevious
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          this.handleNext
        );
      }

      this.variantSelectors.forEach((selector) => {
        selector.addEventListener(
          'change',
          this.handleVariantChange
        );
      });

      this.forms.forEach((form) => {
        form.addEventListener(
          'submit',
          this.handleFormSubmit
        );
      });

      if (Shopify.designMode) {
        document.addEventListener(
          'shopify:block:select',
          this.handleBlockSelect
        );
      }
    }

    unbindEvents() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener(
          'click',
          this.handleTabClick
        );
      });

      this.filters.forEach((filter) => {
        filter.removeEventListener(
          'click',
          this.handleFilterClick
        );
      });

      if (this.searchInput) {
        this.searchInput.removeEventListener(
          'input',
          this.handleSearch
        );
      }

      if (this.previousButton) {
        this.previousButton.removeEventListener(
          'click',
          this.handlePrevious
        );
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener(
          'click',
          this.handleNext
        );
      }

      this.variantSelectors.forEach((selector) => {
        selector.removeEventListener(
          'change',
          this.handleVariantChange
        );
      });

      this.forms.forEach((form) => {
        form.removeEventListener(
          'submit',
          this.handleFormSubmit
        );
      });

      document.removeEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );
    }

    /* =====================================================
       3. PESTAÑAS N&D / VET LIFE
       ===================================================== */

    handleTabClick(event) {
      const tab = event.currentTarget;
      const line = tab.dataset.foodTab;

      if (!line || line === this.activeLine) return;

      this.activeLine = line;
      this.activeFilter = 'all';
      this.searchTerm = '';

      if (this.searchInput) {
        this.searchInput.value = '';
      }

      this.setActiveTab(tab);
      this.resetFilters();
      this.updateInterface();
      this.scrollGridToStart();
    }

    setActiveTab(selectedTab) {
      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;

        tab.classList.toggle(
          'is-active',
          isSelected
        );

        tab.setAttribute(
          'aria-selected',
          isSelected ? 'true' : 'false'
        );

        tab.setAttribute(
          'tabindex',
          isSelected ? '0' : '-1'
        );
      });
    }

    /* =====================================================
       4. FILTROS
       ===================================================== */

    handleFilterClick(event) {
      const button = event.currentTarget;
      const filter = button.dataset.foodFilter;
      const group = button.closest(
        '[data-food-filter-group]'
      );

      if (!filter) return;

      if (filter === 'reset') {
        this.activeFilter = 'all';
        this.searchTerm = '';

        if (this.searchInput) {
          this.searchInput.value = '';
        }

        this.resetFilters();
      } else {
        this.activeFilter = filter;
        this.setActiveFilter(button, group);
      }

      this.updateProducts();
      this.scrollGridToStart();
    }

    setActiveFilter(selectedButton, group) {
      const groupFilters = group
        ? Array.from(
            group.querySelectorAll('[data-food-filter]')
          )
        : this.filters;

      groupFilters.forEach((filter) => {
        const isSelected = filter === selectedButton;

        filter.classList.toggle(
          'is-active',
          isSelected
        );

        filter.setAttribute(
          'aria-pressed',
          isSelected ? 'true' : 'false'
        );
      });
    }

    resetFilters() {
      this.filterGroups.forEach((group) => {
        const groupLine =
          group.dataset.foodFilterGroup || '';

        const isActiveGroup =
          !groupLine || groupLine === this.activeLine;

        group.hidden = !isActiveGroup;

        const groupFilters = Array.from(
          group.querySelectorAll('[data-food-filter]')
        );

        groupFilters.forEach((filter) => {
          const isAll =
            filter.dataset.foodFilter === 'all';

          filter.classList.toggle(
            'is-active',
            isAll
          );

          filter.setAttribute(
            'aria-pressed',
            isAll ? 'true' : 'false'
          );
        });
      });
    }

    /* =====================================================
       5. BUSCADOR
       ===================================================== */

    handleSearch(event) {
      this.searchTerm = this.normalizeText(
        event.currentTarget.value
      );

      this.updateProducts();
      this.scrollGridToStart();
    }

    normalizeText(value) {
      return String(value || '')
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    }

    /* =====================================================
       6. ACTUALIZACIÓN GENERAL
       ===================================================== */

    updateInterface() {
      this.updateTabs();
      this.updateFilterGroups();
      this.updateDietaryNotice();
      this.updateProducts();
    }

    updateTabs() {
      this.tabs.forEach((tab) => {
        const isSelected =
          tab.dataset.foodTab === this.activeLine;

        tab.classList.toggle(
          'is-active',
          isSelected
        );

        tab.setAttribute(
          'aria-selected',
          isSelected ? 'true' : 'false'
        );

        tab.setAttribute(
          'tabindex',
          isSelected ? '0' : '-1'
        );
      });
    }

    updateFilterGroups() {
      this.filterGroups.forEach((group) => {
        const groupLine =
          group.dataset.foodFilterGroup || '';

        group.hidden =
          Boolean(groupLine) &&
          groupLine !== this.activeLine;
      });
    }

    updateDietaryNotice() {
      if (!this.dietaryNotice) return;

      this.dietaryNotice.hidden =
        this.activeLine !== 'vetlife';
    }

    /* =====================================================
       7. FILTRADO DE PRODUCTOS
       ===================================================== */

    updateProducts() {
      let visibleCount = 0;
      let lineTotal = 0;

      this.cards.forEach((card) => {
        const cardLine =
          card.dataset.foodLine || '';

        const belongsToLine =
          !this.activeLine ||
          cardLine === this.activeLine;

        if (belongsToLine) {
          lineTotal += 1;
        }

        const matches = this.cardMatches(
          card,
          belongsToLine
        );

        card.hidden = !matches;

        card.classList.toggle(
          'is-filtered-out',
          !matches
        );

        card.setAttribute(
          'aria-hidden',
          matches ? 'false' : 'true'
        );

        if (matches) {
          visibleCount += 1;
        }
      });

      this.updateCount(
        visibleCount,
        lineTotal
      );

      this.updateEmptyState(
        visibleCount === 0
      );

      window.requestAnimationFrame(() => {
        this.updateNavigationState();
      });
    }

    cardMatches(card, belongsToLine) {
      if (!belongsToLine) return false;

      const tags = this.normalizeText(
        card.dataset.foodTags
      );

      const searchContent = this.normalizeText(
        [
          card.dataset.foodSearch,
          card.dataset.foodTitle,
          card.dataset.foodVendor,
          card.dataset.foodTags
        ].join(' ')
      );

      const matchesFilter =
        this.activeFilter === 'all' ||
        tags
          .split(/\s+/)
          .includes(
            this.normalizeText(
              this.activeFilter
            )
          );

      const matchesSearch =
        !this.searchTerm ||
        searchContent.includes(
          this.searchTerm
        );

      return matchesFilter && matchesSearch;
    }

    updateCount(visible, total) {
      if (!this.countElement) return;

      if (visible === total) {
        this.countElement.textContent =
          `${total} ${total === 1 ? 'producto' : 'productos'}`;
      } else {
        this.countElement.textContent =
          `Mostrando ${visible} de ${total}`;
      }

      this.countElement.setAttribute(
        'aria-label',
        `Mostrando ${visible} de ${total} productos`
      );
    }

    updateEmptyState(isEmpty) {
      if (!this.emptyState) return;

      this.emptyState.hidden = !isEmpty;

      this.emptyState.classList.toggle(
        'is-visible',
        isEmpty
      );
    }

    /* =====================================================
       8. CARRUSEL
       ===================================================== */

    handlePrevious() {
      this.scrollByProduct(-1);
    }

    handleNext() {
      this.scrollByProduct(1);
    }

    scrollByProduct(direction) {
      if (!this.grid) return;

      const visibleCard = this.cards.find(
        (card) => !card.hidden
      );

      if (!visibleCard) return;

      const styles =
        window.getComputedStyle(this.grid);

      const gap =
        parseFloat(
          styles.columnGap ||
          styles.gap ||
          '0'
        ) || 0;

      const distance =
        visibleCard.getBoundingClientRect().width +
        gap;

      this.grid.scrollBy({
        left: distance * direction,
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });

      window.setTimeout(() => {
        this.updateNavigationState();
      }, 350);
    }

    scrollGridToStart() {
      if (!this.grid) return;

      this.grid.scrollTo({
        left: 0,
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });

      window.setTimeout(() => {
        this.updateNavigationState();
      }, 300);
    }

    updateNavigationState() {
      if (
        !this.grid ||
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const maximum =
        this.grid.scrollWidth -
        this.grid.clientWidth;

      const hasOverflow = maximum > 4;
      const atBeginning =
        this.grid.scrollLeft <= 4;
      const atEnd =
        this.grid.scrollLeft >= maximum - 4;

      this.previousButton.disabled =
        !hasOverflow || atBeginning;

      this.nextButton.disabled =
        !hasOverflow || atEnd;
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    /* =====================================================
       9. VARIANTES
       ===================================================== */

    handleVariantChange(event) {
      const selector = event.currentTarget;
      const option =
        selector.options[
          selector.selectedIndex
        ];

      const card = selector.closest(
        '[data-food-card]'
      );

      if (!card || !option) return;

      const form = card.querySelector(
        '[data-food-form]'
      );

      const variantInput = form
        ? form.querySelector('input[name="id"]')
        : null;

      if (variantInput) {
        variantInput.value = option.value;
      }

      this.updateVariantPrice(card, option);
      this.updateVariantAvailability(
        card,
        option
      );
      this.updateVariantURL(card, option);
    }

    updateVariantPrice(card, option) {
      const currentPrice = card.querySelector(
        '[data-food-price-current]'
      );

      const comparePrice = card.querySelector(
        '[data-food-price-compare]'
      );

      const price =
        option.dataset.priceFormatted || '';

      const compare =
        option.dataset.comparePriceFormatted || '';

      const isSale =
        option.dataset.onSale === 'true';

      if (currentPrice && price) {
        currentPrice.textContent = price;

        currentPrice.classList.toggle(
          'is-sale',
          isSale
        );
      }

      if (comparePrice) {
        comparePrice.textContent =
          isSale ? compare : '';

        comparePrice.hidden =
          !isSale || !compare;
      }
    }

    updateVariantAvailability(card, option) {
      const available =
        option.dataset.available === 'true';

      const availability = card.querySelector(
        '[data-food-availability]'
      );

      const button = card.querySelector(
        '[data-food-add-button]'
      );

      if (availability) {
        availability.textContent =
          available ? 'Disponible' : 'Agotado';

        availability.classList.toggle(
          'chemarry-cat-food__availability--sold-out',
          !available
        );
      }

      if (button) {
        button.disabled = !available;
        button.setAttribute(
          'aria-disabled',
          available ? 'false' : 'true'
        );

        const buttonText = button.querySelector(
          '[data-food-button-text]'
        );

        if (buttonText) {
          buttonText.textContent =
            available
              ? button.dataset.availableText ||
                'Agregar al carrito'
              : button.dataset.soldOutText ||
                'Agotado';
        }
      }
    }

    updateVariantURL(card, option) {
      const variantURL =
        option.dataset.variantUrl;

      if (!variantURL) return;

      const productLinks = card.querySelectorAll(
        '[data-food-product-link]'
      );

      productLinks.forEach((link) => {
        link.href = variantURL;
      });
    }

    /* =====================================================
       10. AGREGADO AL CARRITO
       ===================================================== */

    async handleFormSubmit(event) {
      event.preventDefault();

      const form = event.currentTarget;
      const button = form.querySelector(
        '[data-food-add-button]'
      );

      if (!button || button.disabled) return;

      const variantInput =
        form.querySelector('input[name="id"]');

      if (
        !variantInput ||
        !variantInput.value
      ) {
        this.showToast(
          'Selecciona una variante válida.',
          true
        );
        return;
      }

      this.setButtonLoading(button, true);

      try {
        const formData = new FormData(form);
        const cartDrawer =
          document.querySelector('cart-drawer');

        const cartNotification =
          document.querySelector(
            'cart-notification'
          );

        const sections = this.getCartSections(
          cartDrawer,
          cartNotification
        );

        if (sections.length) {
          formData.set(
            'sections',
            sections.join(',')
          );

          formData.set(
            'sections_url',
            window.location.pathname
          );
        }

        const response = await fetch(
          this.getCartAddURL(),
          {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With':
                'XMLHttpRequest'
            },
            body: formData
          }
        );

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
            result.message ||
            'No pudimos agregar el producto.'
          );
        }

        await this.renderCartResult(
          result,
          cartDrawer,
          cartNotification
        );

        this.showToast(
          button.dataset.successText ||
          'Producto agregado al carrito.'
        );

        this.dispatchEvent(
          new CustomEvent(
            'chemarry:product-added',
            {
              bubbles: true,
              detail: {
                product: result
              }
            }
          )
        );
      } catch (error) {
        console.error(
          '[Chemarry Cat Food]',
          error
        );

        this.showToast(
          error.message ||
          'No pudimos agregar el producto. Intenta nuevamente.',
          true
        );
      } finally {
        this.setButtonLoading(button, false);
      }
    }

    getCartAddURL() {
      if (
        window.routes &&
        window.routes.cart_add_url
      ) {
        return `${window.routes.cart_add_url}.js`;
      }

      return '/cart/add.js';
    }

    getCartSections(
      cartDrawer,
      cartNotification
    ) {
      const component =
        cartDrawer || cartNotification;

      if (
        component &&
        typeof component.getSectionsToRender ===
          'function'
      ) {
        return component
          .getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean);
      }

      return [
        'cart-drawer',
        'cart-icon-bubble'
      ];
    }

    async renderCartResult(
      result,
      cartDrawer,
      cartNotification
    ) {
      if (
        cartDrawer &&
        typeof cartDrawer.renderContents ===
          'function'
      ) {
        cartDrawer.renderContents(result);

        window.setTimeout(() => {
          if (
            typeof cartDrawer.open ===
            'function'
          ) {
            cartDrawer.open();
          }
        }, 80);

        return;
      }

      if (
        cartNotification &&
        typeof cartNotification.renderContents ===
          'function'
      ) {
        cartNotification.renderContents(result);
        return;
      }

      await this.refreshCartSections();
    }

    async refreshCartSections() {
      const sectionNames = [
        'cart-drawer',
        'cart-icon-bubble'
      ];

      const url =
        `/cart?sections=${sectionNames.join(',')}`;

      try {
        const response = await fetch(url);
        const sections = await response.json();

        this.replaceSectionHTML(
          'cart-icon-bubble',
          sections['cart-icon-bubble']
        );

        const drawer = document.querySelector(
          'cart-drawer'
        );

        if (
          drawer &&
          sections['cart-drawer']
        ) {
          const parser =
            new DOMParser();

          const documentHTML =
            parser.parseFromString(
              sections['cart-drawer'],
              'text/html'
            );

          const newDrawer =
            documentHTML.querySelector(
              'cart-drawer'
            );

          if (newDrawer) {
            drawer.replaceWith(newDrawer);

            window.setTimeout(() => {
              const refreshedDrawer =
                document.querySelector(
                  'cart-drawer'
                );

              if (
                refreshedDrawer &&
                typeof refreshedDrawer.open ===
                  'function'
              ) {
                refreshedDrawer.open();
              }
            }, 100);
          }
        }
      } catch (error) {
        console.warn(
          '[Chemarry Cat Food] No fue posible actualizar el carrito lateral.',
          error
        );
      }
    }

    replaceSectionHTML(id, html) {
      if (!html) return;

      const current =
        document.getElementById(id);

      if (!current) return;

      const parser = new DOMParser();
      const parsed = parser.parseFromString(
        html,
        'text/html'
      );

      const replacement =
        parsed.getElementById(id);

      if (replacement) {
        current.replaceWith(replacement);
      }
    }

    setButtonLoading(button, loading) {
      button.classList.toggle(
        'is-loading',
        loading
      );

      button.disabled = loading;

      button.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );
    }

    /* =====================================================
       11. MENSAJES
       ===================================================== */

    showToast(message, isError = false) {
      if (!this.toast) return;

      const text = this.toast.querySelector(
        '[data-food-toast-text]'
      );

      if (text) {
        text.textContent = message;
      } else {
        this.toast.textContent = message;
      }

      this.toast.classList.toggle(
        'is-error',
        isError
      );

      this.toast.classList.add(
        'is-visible'
      );

      this.toast.setAttribute(
        'role',
        isError ? 'alert' : 'status'
      );

      if (this.toastTimer) {
        window.clearTimeout(
          this.toastTimer
        );
      }

      this.toastTimer =
        window.setTimeout(() => {
          this.toast.classList.remove(
            'is-visible'
          );
        }, 3200);
    }

    /* =====================================================
       12. EDITOR DE SHOPIFY
       ===================================================== */

    handleBlockSelect(event) {
      const block = event.target;

      if (
        !block ||
        !this.contains(block)
      ) {
        return;
      }

      const card = block.matches(
        '[data-food-card]'
      )
        ? block
        : block.querySelector(
            '[data-food-card]'
          );

      if (!card) return;

      const cardLine =
        card.dataset.foodLine;

      if (
        cardLine &&
        cardLine !== this.activeLine
      ) {
        this.activeLine = cardLine;
        this.activeFilter = 'all';
        this.searchTerm = '';

        if (this.searchInput) {
          this.searchInput.value = '';
        }

        this.resetFilters();
        this.updateInterface();
      }

      window.requestAnimationFrame(() => {
        card.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      });
    }
  }

  customElements.define(
    'chemarry-cat-food',
    ChemarryCatFood
  );
})();